import { RequestProvider } from '@/shared/providers/request.provider';
import { ErrorProvider } from '@/shared/providers/errors.provider';
import { HOMEWORKS_API } from '../values/homeworks.values';
import { RequestCreateHomework } from '../models/request-create-homework.model';
import { IRequestStudentHomeworksFromTeacherWidget } from '../interfaces/request-student-homeworks-from-teacher.interface';
import { IRequestPersonsHomeworksFromTeacherWidget } from '../interfaces/request-persons-homeworks-from-teacher.interface';
import { IRequestSetDraftHomework } from '../interfaces/request-set-draft-homework.interface';
import { IRequestSetFeedbacDraft } from '../interfaces/request-set-feedback-draft.interface';
import { IRequestRegistryWhiteboardSocket } from '../interfaces/request-registry-whiteboard-socket.interface'; // Restored
import { IWhiteboardUser } from '../interfaces/whiteboard-user.interface';
import { IRequestHomeworkBySectionStudent } from '../interfaces/request-homework-by-section-student.interface';
import { IRequestHomeworkStatusByStudent } from '../interfaces/request-homework-status-by-student.interface';
import HomeworkBoardDraftResponse from '@/homework/models/homework-board-draft-response.model';
import IRequestHomeworkBoardGroup from '@/homework/interfaces/request-homework-board-group.interface';
import HomeworkBoardVersion from '../models/homework-board-version.model';
import BoardAnnotations from '@/homework/models/board-annotations.model';
import {HomeworkStatusStudent} from '@/homework/models/homework-status-student-response.model';
import HomeworkHistoryRecoverResponse from '../models/homework-history-recover-response.model';
import { WboPersistenceLocatorProvider } from '@/components/common/ed-board/ed-board-tools/wbo-client/persistence/wbo-persistence-locator-provider';



class HomeworksServiceClass {

  async createHomework(req: RequestCreateHomework, file: any = null): Promise<any> {
    try {
      let data = new FormData();
      data.append('homework', JSON.stringify(new RequestCreateHomework(req)));
      if(file) data.append('homeworkResource', file, file.name);
      return RequestProvider.post(HOMEWORKS_API.CREATE_HOMEWORK, data);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async getCoursesHomeworksByTeacher(
    page: number = 0,
    pageSize: number = 10,
    groupBySections :boolean = false,
    classId: number = null,
    courseId: number = null,
    sectionId: number = null): Promise<any> {
    try {
      let req = `${HOMEWORKS_API.GET_COURSES_HOMEWORKS_BY_TEACHER}/?page=${page}&pageSize=${pageSize}&groupBySections=${groupBySections}`;
      let classReq = classId ? `&classId=${classId}` : '';
      let courseReq = courseId ? `&courseId=${courseId}` : '';
      let sectionReq = sectionId ? `&sectionId=${sectionId}` : '';
      return RequestProvider.get(`${req}${classReq}${courseReq}${sectionReq}`);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async getStudentsWithHomeworksFromCourse(classId: number, contentId: number, page: number = 0, pageSize: number = 10): Promise<any> {
    try {
      const path = HOMEWORKS_API.GET_STUDENTS_WITH_HOMEWORKS_FROM_COURSE
        .replace('{classId}', String(classId))
        .replace('{contentId}', String(contentId));
      return RequestProvider.get(`${path}/?page=${page}&pageSize=${pageSize}`);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async getStudentHomeworksFromTeacherWidget(req: IRequestStudentHomeworksFromTeacherWidget) {
    try {
      const path = HOMEWORKS_API.GET_STUDENT_HOMEWORKS_FROM_TEACHER_WIDGET
        .replace('{classId}', String(req.classId))
        .replace('{contentId}', String(req.contentId))
        .replace('{studentId}', String(req.studentId));
      return RequestProvider.get(`${path}/?page=${req.page}&pageSize=${req.pageSize}`);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async getTeacherSectionsFromCourseHomeworks(classId: number, contentId: number, page: number = 0, pageSize: number = 10) {
    try {
      const path = HOMEWORKS_API.GET_TEACHER_SECTIONS_FROM_COURSE_HOMEWORK
        .replace('{classId}', String(classId))
        .replace('{contentId}', String(contentId))
      return RequestProvider.get(`${path}/?page=${page}&pageSize=${pageSize}`);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async getSectionsHomeworksFromTeacherWidget(req: IRequestPersonsHomeworksFromTeacherWidget) {
    try {
      const path = HOMEWORKS_API.GET_SECTIONS_HOMEWORKS_FROM_TEACHER_WIDGET
        .replace('{classId}', String(req.classId))
        .replace('{contentId}', String(req.contentId))
        .replace('{sectionId}', String(req.sectionId))
      return RequestProvider.get(
        `${path}/?page=${req.page}&pageSize=${req.pageSize}` + (req.includeArchived?'&includeArchived=true':'')
        + ((req.status && req.status.length > 0) ? '&includeStatus=' + req.status.join('&includeStatus=') : ''))
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  // en vez de grade va grades con gradingDetails: { type: POINTS o PERCENTAGE, SCALE }, type obj cada objeto
  async updateHomework(req: RequestCreateHomework, file: any = null, isResponse = false): Promise<any> {
    try {
      let data = new FormData();
      let path = HOMEWORKS_API.UPDATE_HOMEWORK.replace('{homeworkId}', String(req.id));
      data.append('homework', JSON.stringify(new RequestCreateHomework(req)));
      data.append('isResponse', String(isResponse));
      if(file) data.append('homeworkResource', file, file.name);
      return RequestProvider.put(path, data)
    } catch (error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async updateStatus(homeworkId: number, status: string) {
    try {
      let path = HOMEWORKS_API.SET_TEACHER_HOMEWORK_STATUS_FEEDBACK_DRAFT;
      if (status == 'FINISHED')
        path = HOMEWORKS_API.SET_TEACHER_HOMEWORK_STATUS_FINISHED;
      else if (status == 'SEEN')
        path = HOMEWORKS_API.SET_TEACHER_HOMEWORK_STATUS_SEEN;
      path = path.replace('{homeworkId}', String(homeworkId));
      return RequestProvider.put(path)
    } catch (error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async setGrade(homeworkId: number, grade: number) {
    try {
      let path = HOMEWORKS_API.SET_TEACHER_HOMEWORK_GRADE
        .replace('{homeworkId}', String(homeworkId))
        .replace('{grade}', String(grade));
      return RequestProvider.put(path)
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async getCoursesHomeworksByStudent(): Promise<any> {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_COURSES_HOMEWORKS_BY_STUDENT);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }
  getHomeworkScale(homeworkId: number) {
    return new Promise((resolve, reject) => {
      let path = HOMEWORKS_API.GET_HOMEWORK_SCALE
        .replace('{homeworkId}', String(homeworkId))
      RequestProvider.get(path).then(res => {
        resolve(res);
      }).catch(err => {
        reject(err);
      }); 
    });
  }

  async getStudentPendingHomeworks(classId: number, contentId: number, page: number = 0, pageSize: number = 10): Promise<any> {
    try {
      const req = HOMEWORKS_API.GET_STUDENT_PENDING_HOMEWORKS
        .replace('{classId}', String(classId))
        .replace('{contentId}', String(contentId));
      return RequestProvider.get(`${req}/?page=${page}&pageSize=${pageSize}`);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  async setHomeworkSolved(homeworkId: number) {
    try {
      let req = HOMEWORKS_API.FINISH_HOMEWORK.replace('{homeworkId}', String(homeworkId))
      return RequestProvider.put(req);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  setHomeworkDraft(homework: IRequestSetDraftHomework, file: any) {
    try {
      let data = new FormData();
      data.append('homework', JSON.stringify(homework));
      data.append('homeworkResource', file);
      return RequestProvider.post(HOMEWORKS_API.CREATE_DRAFT_HOMEWORK, data);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  setFeedbackDraft(homework: IRequestSetFeedbacDraft, file: any) {
    try {
      let data = new FormData();
      data.append('homework', JSON.stringify(homework));
      data.append('homeworkResource', file);
      return RequestProvider.post(HOMEWORKS_API.SET_FEEDBACK_DRAFT, data);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getStudentHomeworksAllFromTeacherWidget(req: IRequestStudentHomeworksFromTeacherWidget) {
    try {
      const path = HOMEWORKS_API.GET_STUDENT_HOMEWORKS_ALL_FROM_TEACHER_WIDGET
        .replace('{classId}', String(req.classId))
        .replace('{contentId}', String(req.contentId))
        .replace('{studentId}', String(req.studentId));
      return RequestProvider.get(`${path}/?page=${req.page}&pageSize=${req.pageSize}`);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getCurrentHomeworkDraft(sectionId: number, studentId: number): Promise<any> {
    try {
      const path = HOMEWORKS_API.GET_CURRENT_HOMEWORK_DRAFT
        .replace('{sectionId}', sectionId.toString())
        .replace('{studentId}', studentId.toString());
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  /* Board services */
  public async obtainTeacherWhiteboardHomeworkHash(payload): Promise<any> {
    try {
      let response: any = await RequestProvider.post(HOMEWORKS_API.OBTAIN_TEACHER_WHITEBOARD_HOMEWORK_HASH, payload);
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async obtainStudentWhiteboardHomeworkHash(payload): Promise<any> {
    try {
      let response: any = await RequestProvider.post(HOMEWORKS_API.OBTAIN_STUDENT_WHITEBOARD_HOMEWORK_HASH, payload)
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async obtainWhiteboardHomeworkFeedbackHash(homeworkId): Promise<any> {
    try {
      const path = HOMEWORKS_API.OBTAIN_WHITEBOARD_HOMEWORK_FEEDBACK_HASH
        .replace('{homeworkId}', homeworkId);
      return RequestProvider.post(path, null);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async registryWhiteBoardSocketId(req: IRequestRegistryWhiteboardSocket) : Promise<any> {
    try {
      let response: any = await RequestProvider.post(HOMEWORKS_API.REGISTRY_WHITEBOARD_SOCKET, req)
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async obtaninCurrentWhiteboardUsers(boardGroup: string) : Promise<IWhiteboardUser[]> {
    try {
      const path = HOMEWORKS_API.OBTAIN_CURRENT_WHITEBOARD_USERS.replace('{boardGroup}', boardGroup)
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async obtainUsersMadeAnnotations(boardGroup: string): Promise<IWhiteboardUser[]> {
    try {
      const path = HOMEWORKS_API.OBTAIN_USERS_MADE_ANNOTATIONS.replace('{boardGroup}', boardGroup)
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async addWhiteBoardPage(boardGroupHash: string, templateName: string): Promise<any> {
    try {
      let response: any = await RequestProvider.post(HOMEWORKS_API.ADD_BOARD_PAGE, {boardGroupHash, templateName});
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async obtainBoardUpdates(boardGroup: string, pageNumber: number) : Promise<any> {
    try {
      const path = HOMEWORKS_API.OBTAIN_BOARD_UPDATES
      .replace('{boardGroup}', boardGroup)
      .replace('{pageNumber}', String(pageNumber));
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getHomeworkConverterStatus(homeworkHash: string): Promise<any> {
    try {
      const path: string = HOMEWORKS_API.GET_CURRENT_HOMEWORK_CONVERTER_STATUS
        .replace('{hash}', homeworkHash);
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getHomeworkBySectionStudent(request : IRequestHomeworkBySectionStudent): Promise<any> {
    try {
      const path = HOMEWORKS_API.GET_HOMEWORK_BY_SECTION_STUDENT
        .replace('{classId}', String(request.classId))
        .replace('{contentId}', String(request.contentId))
        .replace('{sectionId}', String(request.sectionId))
        .replace('{studentId}', String(request.studentId))
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getStudentsHomeworkStatus(request: IRequestHomeworkStatusByStudent) {
    try {
      return RequestProvider.get(
        `${HOMEWORKS_API.GET_HOMEWORK_STATUS_BY_STUDENTS}?classId=${request.classId}&contentId=${request.contentId}&sectionId=${request.sectionId}&studentIds=${request.studentIds}`
      );
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getHomeworkStatus(homeworkId: number): Promise<HomeworkStatusStudent> {
    try {
      const path = HOMEWORKS_API.GET_HOMEWORK_STATUS
          .replace('{homeworkId}', String(homeworkId));
      return RequestProvider.get(path);
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getHomeworkBoardPages(boardGroupHash: string) {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_HOMEWORK_BOARD_PAGES.replace('{boardGroup}', boardGroupHash));
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async getHomeworkHistoryBoardPages(historyId: string) {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_HOMEWORK_HISTORY_BOARD_PAGES.replace('{historyId}', historyId));
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public obtainBoardAnnotations(boardGroupHash: string) : Promise<BoardAnnotations> {
    try {
      return RequestProvider.get(HOMEWORKS_API.OBTAIN_BOARD_ANNOTATIONS.replace('{boardGroup}', boardGroupHash));
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public getBoardCurrentVersion(boardGroup: string): Promise<HomeworkBoardVersion> {
    return new Promise<HomeworkBoardVersion>((resolve, reject) => {
      RequestProvider.get(HOMEWORKS_API.GET_BOARD_CURRENT_VERSION
          .replace('{boardGroup}', boardGroup))
          .then((res: HomeworkBoardVersion) => resolve(res))
          .catch(reason => reject(reason));
    });
  }

  public updateHomeworkBoardAnnotations(payload: IRequestHomeworkBoardGroup, clientId: string): Promise<HomeworkBoardDraftResponse> {
    return this.updateBoardAnnotations(HOMEWORKS_API.UPDATE_BOARD_ANNOTATIONS, clientId, payload);
  }

  public updateFeedbackBoardAnnotations(payload: IRequestHomeworkBoardGroup, clientId: string): Promise<HomeworkBoardDraftResponse> {
    return this.updateBoardAnnotations(HOMEWORKS_API.UPDATE_FEEDBACK_BOARD_ANNOTATIONS, clientId, payload);
  }

  public updateBoardAnnotations(serviceName: string, clientId: string, payload: IRequestHomeworkBoardGroup): Promise<HomeworkBoardDraftResponse> {
    return new Promise<HomeworkBoardDraftResponse>((resolve, reject) => {
      let path = serviceName;
      if(clientId){
        path = path.concat(`?clientId=${clientId}`);
      }
      RequestProvider.post(path, payload)
          .then((res: any) => resolve(res))
          .catch(reason => reject(reason));
    });
  }

  /* Board services end */

  /* History Homework */
  /**
   * GET_HOMEWORK_HISTORY: 'homework/history/{classId}/{sectionId}/{studentId}/{type}'
   * @param classId
   * @param sectionId
   * @param studentId
   * @param type
   */
  public getHomeworkHistory(classId: string, sectionId: string, studentId: string, type: string){
    return new Promise((resolve, reject) => {
      const req = HOMEWORKS_API.GET_HOMEWORK_HISTORY
          .replace('{classId}', classId)
          .replace('{sectionId}', sectionId)
          .replace('{studentId}', studentId)
          .replace('{type}', type)
      RequestProvider.get(req).then(res => {
        resolve(res);
      }).catch(err => {
        reject(err);
      });
    });
  }

  public obtainHistoryBoardAnnotations(historyId: string) : Promise<BoardAnnotations> {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_HOMEWORK_HISTORY_ANNOTATIONS.replace('{historyId}', historyId));
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public async recoverHistoryBoard(historyId: string) : Promise<HomeworkHistoryRecoverResponse> {
    try {
      const response = await RequestProvider.post(HOMEWORKS_API.RECOVER_HOMEWORK_HISTORY.replace('{historyId}', historyId)) as HomeworkHistoryRecoverResponse;
      await this.deleteLocalBoardFiles(response);
      return response;
    } catch(error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  private async deleteLocalBoardFiles(response: HomeworkHistoryRecoverResponse) {
    for( let boardName of response.updatedBoardList ) {
      const wboPersistenceProvider = WboPersistenceLocatorProvider.getInstance(boardName);
      await wboPersistenceProvider.deleteAnnotationsFile();
    }
  }

  public obtainTextDraftVersion(sectionId: number, studentId: number, classId: number,version: string): Promise<any> {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_HOMEWORK_HISTORY_TEXT_DRAFT
        .replace('{historyId}', version)
        .replace('{sectionId}', String(sectionId))
        .replace('{studentId}', String(studentId))
        .replace('{classId}', String(classId)))
    } catch (error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public obtainTypeDraftInfo(classId: number, sectionId: number, studentId: number): Promise<any> {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_HOMEWORK_TEXT_DRAFT_INFO
        .replace('{classId}', String(classId))
        .replace('{sectionId}', String(sectionId))
        .replace('{studentId}', String(studentId)))
    } catch (error) {
      throw ErrorProvider.getError(error.error);
    }
  }

  public getConsolidatedHomeworksByClass(classId: number, contentId: number): Promise<any> {
    try {
      return RequestProvider.get(HOMEWORKS_API.GET_CONSOLIDATED_HOMEWORKS_BY_CLASS
        .replace('{classId}', String(classId))
        .replace('{contentId}', String(contentId)))
    } catch (error) {
      throw ErrorProvider.getError(error.error);
    }
  }
  public createConsolidatedHomeworks(classId: number, contentId: number, studentId: number, includeFeedback: boolean = false, includeProblem: boolean = false): Promise<any> {
    try {
      return RequestProvider.put(HOMEWORKS_API.GENERATE_CONSOLIDATED_HOMEWORKS
        .replace('{classId}', String(classId))
        .replace('{contentId}', String(contentId))
        .replace('{studentId}', String(studentId))
        .replace('{includeFeedback}', String(includeFeedback))
        .replace('{includeProblem}', String(includeProblem)))
    } catch (error) {
      throw ErrorProvider.getError(error.error);
    }
  }
}

export const HomeworksService = new HomeworksServiceClass();
